/* eslint-disable no-undefined */
import { algoliasearch } from 'algoliasearch';
import instantsearch from 'instantsearch.js';
import { baseConfig } from '../config/algolia';
import { logInfo } from './logging';

/**
 * Algolia search class
 */
export class Algolia {
  /**
   * Class Constructor.
   *
   * @param key
   * @param customConfig
   */
  constructor(key, customConfig) {
    this.lastNextPageDate = 0;
    this.key = key;

    // Set up our config with defaults
    this.prefix = `${key}-search`;
    const config = Object.assign(
      baseConfig,
      {
        pager: 'infinitescroll',
        initialized: false,
        renderScrollTo: false,
        facets: [
          // The Availabilty and Category facet is added automatically on quicksearches here
          {
            facet: 'Brand',
            type: 'refinementList',
          },
          {
            facet: 'Price',
            type: 'priceRanges',
          },
        ],
      },
      customConfig
    );
    config.searchParameters = config.searchParameters ? config.searchParameters : {};
    config.sortMethod = config.sortMethod || '';
    config.searchParameters.hitsPerPage = customConfig.pageSize ? customConfig.pageSize : 36;
    config.searchParameters.filters = this.buildSearchFilters(config.searchParameters.filters);

    if (this.key === 'collection' && Boolean(config.searchParameters.query)) {
      config.searchParameters.query = config.searchParameters.query;
      config.searchParameters.restrictSearchableAttributes = 'title,facets.Artist';
      config.searchParameters.typoTolerance = false;
      config.searchParameters.attributesToHighlight = '';
    }

    logInfo(config.searchParameters.filters, 'Algolia filters sent');
    // This is annoying, but we need to prevent deep cloning
    this.config = JSON.parse(JSON.stringify(config));
    this.config.searchParameters.facets = this.config.activeFacets;
    this.lastLoadMore = 0;
  }

  /**
   * Initiate an Algolia Instantsearch search.
   */
  run() {
    // Set up instantsearch
    const that = this;
    this.indexName = `${this.config.index_products}`;
    this.search = instantsearch({
      appId: this.config.app_id,
      apiKey: this.config.search_api_key,
      indexName: `${this.indexName}${this.config.sortMethod}`,
      urlSync: {
        mapping: { synonyms: 's' },
      },
      searchParameters: this.config.searchParameters,
      searchFunction: that[`${that.key}SearchFunction`]
        ? function (helper) {
            const fxn = that[`${that.key}SearchFunction`];
            fxn(that, helper);
          }
        : undefined,
    });
    this.helper = this.search.helper;
    this.facetItemTemplates = {
      refinementItem: require('../handlebars/algolia/facet-item.handlebars'),
      refinementList: require('../handlebars/algolia/facet-item.handlebars'),
      priceRanges: require('../handlebars/algolia/facet-item-pricerange.handlebars'),
      starRating: require('../handlebars/algolia/facet-item-stars.handlebars'),
    };

    // Get the Sort options from our config
    // Taken from algolia_sort_order.js.liquid file generated by the Shopify Algolia App
    const label = this.key === 'collection' ? 'Popular' : 'Relevancy';
    const sortOrders = [{ name: this.indexName, label }];
    this.config.sort_orders.forEach(sortOrder => {
      if (sortOrder.asc && (sortOrder.asc.active === true || sortOrder.asc.active === '1')) {
        sortOrders.push({
          name: `${this.indexName}_${sortOrder.key}_asc`,
          label: sortOrder.asc.title,
        });
      }
      if (sortOrder.desc && (sortOrder.desc.active === true || sortOrder.desc.active === '1')) {
        sortOrders.push({
          name: `${this.indexName}_${sortOrder.key}_desc`,
          label: sortOrder.desc.title,
        });
      }
    });
  }
}

/**
 * This helper search class is used on collection pages to get the facets before
 * we build the Algolia search.
 *
 * @param string index : the index name to query
 * @param object query : the parameters to search
 * @param function callback
 */
export class AlgoliaHelperSearch {
  constructor(index, params, cb, fail) {
    this.config = baseConfig;

    this.index = index;
    this.params = params;

    this.run(cb, fail);
  }

  run(cb, fail) {
    const client = algoliasearch(this.config.app_id, this.config.search_api_key);
    const query = this.params.query || '';

    client
      .searchSingleIndex({ indexName: this.index, searchParams: { query, ...this.params } })
      .then(content => {
        return cb(content);
      })
      .catch(err => {
        fail(err);
      });
  }
}

export const searchAlgoliaIndex = params => {
  const client = algoliasearch(baseConfig.app_id, baseConfig.search_api_key);
  const query = params.query || '';

  return client.searchSingleIndex({ indexName: baseConfig.index_products ?? '', searchParams: { query, ...params } });
};
